import { Divider, Stack, SxProps, Theme, Typography } from '@mui/material';
import { ReactElement } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { TOOLTIP_PAPER_STYLE_SLOT_PROPS } from 'src/components/Tooltip/tooltip-paper-style-slot-props';
import { Address } from 'src/graphql';
import { toSxArray } from 'src/utils/sx';
import { getAddressTooltipLines } from './site-address-lines';

export const SiteAddress = ({
  address,
  onClick,
  tooltipText,
  icon,
  textSx,
}: {
  address: Address;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  tooltipText?: string | ((children: React.ReactNode) => React.ReactNode);
  /** Replaces the default brand glyph, e.g. when the address renders as a property row. */
  icon?: ReactElement;
  textSx?: SxProps<Theme>;
}) => {
  let tooltipTitle: React.ReactNode | undefined;

  if (onClick) {
    if (typeof tooltipText === 'function') {
      tooltipTitle = tooltipText(address?.fullAddress);
    } else if (tooltipText !== undefined) {
      const lines = getAddressTooltipLines(address);
      tooltipTitle = (
        <Stack direction="column" spacing={1} p={1}>
          {lines.map(({ id, text }) => (
            <Typography key={id} variant="body2">
              {text}
            </Typography>
          ))}
          {lines.length > 0 && <Divider />}
          <Typography variant="body2" color="text.secondary">
            {tooltipText}
          </Typography>
        </Stack>
      );
    } else {
      const lines = getAddressTooltipLines(address);
      tooltipTitle =
        lines.length > 0
          ? lines.map(({ id, text }) => (
              <Typography key={id} variant="body2">
                {text}
              </Typography>
            ))
          : undefined;
    }
  }

  return (
    <>
      <Tooltip title={address?.brand?.name} hideWhenRefIsHidden>
        {icon ?? (
          <DynamicIcon
            name={
              address?.brand?.name.toLowerCase().includes('commercial') ? 'domain' : 'home'
            }
            weight={500}
            filled
            fontSize="1.25rem"
            sx={{ cursor: 'default' }}
          />
        )}
      </Tooltip>
      <Tooltip
        hideWhenRefIsHidden
        slotProps={TOOLTIP_PAPER_STYLE_SLOT_PROPS}
        title={tooltipTitle}
      >
        <Typography
          variant="subtitle2"
          paddingBlock={1}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          overflow="hidden"
          sx={[onClick ? { cursor: 'pointer' } : false, ...toSxArray(textSx)]}
          onClick={onClick}
        >
          {address?.fullAddress}
        </Typography>
      </Tooltip>
    </>
  );
};
